/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import FinanceService, { CanAffordHome } from '../../services/finance.service';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import UtilsService from '../../services/utils.service';

// components
import BwSlider from '../bw-slider/BwSlider';
import { Collapsible, CollapsibleContent, CollapsibleHeading, FieldControl, Label } from '@americanexpress/dls-react';
import GoalDrawer from './GoalDrawer';
import InfoCallout from '../callout/InfoCallout';
import Output from '../atomic/molecules/output/Output';
import SavingsGoal from 'src/bw/bw-drawer/common/SavingsGoal';

// icons and css
import { ReactComponent as SoldSignIcon } from 'src/bw/goal/sold-sign.svg';
import { HELP_DOMAIN } from 'src/utils/constants';
import CardService from '../../services/card.service';
import BWRadioGroup from '../bw-radio-group/BWRadioGroup';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import layoutStyles from 'src/styles/layout.module.scss';
import styles from './BuyHomeDrawer.module.scss';

export interface FeesInterface {
    name: string;
    attribute: string;
}

export const feesList: FeesInterface[] = [
    {
        name: 'HOA',
        attribute: 'hoa'
    },
    {
        name: 'property tax',
        attribute: 'property_tax'
    },
    {
        name: 'homeowner’s insurance',
        attribute: 'property_insurance'
    },
    {
        name: 'renovation budget',
        attribute: 'renovation_budget'
    }
];

export default class BuyHomeDrawer extends Component<
    any,
    {
        showAdvanced: boolean;
        showProperty: boolean;
        mortgageInterest: string | number;
    }
> {
    public override state = {
        showAdvanced: false,
        showProperty: false,
        mortgageInterest: '0'
    };

    public static getDerivedStateFromProps(props: any, prevState: any): any {
        if (props.drawerProps.card.id !== prevState.cardId) {
            return {
                cardId: props.drawerProps.card.id,
                mortgageInterest: props?.drawerProps?.card?.attributes?.mortgage_interest?.toString()
            };
        } else {
            return prevState;
        }
    }

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = drawerProps.card;
        // snake case because of attribute names
        /* eslint-disable  @typescript-eslint/naming-convention */
        let purchase_price: number = 0;
        let renovation_budget: number = 0;
        let down_payment_percent: number = 0;
        let down_payment: number = 0;
        let mortgage_type: number = 30;
        let mortgage_interest: number = 0;
        let property_insurance: number = 0;
        let property_tax: number = 0;
        let borrowing: number = 0;
        let monthly_payment: number = 0;
        let hoa: number = 0;
        let existing_profit: number = 0;

        const attributes: any = card.attributes;
        if (attributes) {
            purchase_price = attributes.purchase_price;
            down_payment = attributes.down_payment;
            existing_profit = attributes.existing_profit || 0;
            renovation_budget = attributes.renovation_budget;
            down_payment_percent = Math.ceil((down_payment / purchase_price) * 100);
            mortgage_type = attributes.mortgage_type;
            mortgage_interest = attributes.mortgage_interest;
            property_insurance = attributes.property_insurance;
            property_tax = attributes.property_tax;
            borrowing = Math.max(purchase_price + renovation_budget - down_payment, 0);
            hoa = attributes.hoa || 0;

            monthly_payment =
                FinanceService.getMonthlyPayment(borrowing, mortgage_interest, mortgage_type) +
                property_tax / 12 +
                property_insurance / 12 +
                hoa / 12;
            monthly_payment = Math.max(monthly_payment, 0);

            // update attributes that are calculated, and not directly modified by dialog, so they go downstream
            attributes.down_payment = down_payment;
            attributes.monthly_payment = monthly_payment;
            attributes.borrowing = borrowing;
        }

        const included = [];
        const notIncluded = [];

        for (const fee of feesList) {
            if (attributes[fee.attribute]) {
                included.push(fee.name);
            } else {
                notIncluded.push(fee.name);
            }
        }

        let feesString: string;
        if (included.length) {
            if (notIncluded.length) {
                feesString =
                    `Including ${UtilsService.formatList(included, 'and')}, ` +
                    `but not including ${UtilsService.formatList(notIncluded, 'or')}.`;
            } else {
                feesString = `Including ${UtilsService.formatList(included, 'and')}.`;
            }
        } else {
            if (notIncluded.length) {
                feesString = `Not including ${UtilsService.formatList(notIncluded, 'or')}.`;
            }
        }

        const cashOnPurchase: number = down_payment - existing_profit;
        const canAfford: CanAffordHome = FinanceService.canAffordHome(
            card.startDate,
            cashOnPurchase,
            monthly_payment,
            (card as any).monthlySavings,
            (card as any).monthlyGrossIncome,
            (card as any).monthlyFreeCash
        );

        const helpUrl: string = HELP_DOMAIN ? HELP_DOMAIN + '/articles/buy-new-home' : null;

        const propertyDiv: JSX.Element = (
            <div className={`${layoutStyles.drawerSection} ${styles.collapsibleContent}`}>
                <BwSlider
                    id="existing-profit-slider"
                    label="Profit from sale"
                    value={existing_profit}
                    formattedValue={FormatService.formatCompactMoney(existing_profit)}
                    step={10000}
                    min={0}
                    max={3000000}
                    onChange={(value: any) =>
                        this.props.handleChangeCardAttribute(card, value, 'existing_profit', 'currency')
                    }
                    inputToggleText="Add a precise cost"
                />
                <InfoCallout
                    noMargin
                    text="Take the value of your home and subtract the estimated balance of your loan."
                />
            </div>
        );

        const advancedDiv: JSX.Element = (
            <div className={`${layoutStyles.drawerLayout} ${styles.collapsibleContent}`}>
                <FieldControl>
                    <Label htmlFor="propertyTax">Annual estimated property tax</Label>
                    <BWCurrencyInput
                        fullWidth
                        id="propertyTax"
                        defaultValue={0}
                        value={property_tax}
                        showFractions={false}
                        onChange={(value: number) =>
                            this.props.handleChangeCardAttribute(drawerProps.card, value, 'property_tax', 'currency')
                        }
                    />
                </FieldControl>

                <FieldControl>
                    <Label htmlFor="homeInsurance">Annual home insurance</Label>
                    <BWCurrencyInput
                        fullWidth
                        id="homeInsurance"
                        defaultValue={0}
                        value={property_insurance}
                        showFractions={false}
                        onChange={(value: number) =>
                            this.props.handleChangeCardAttribute(
                                drawerProps.card,
                                value,
                                'property_insurance',
                                'currency'
                            )
                        }
                    />
                </FieldControl>

                <FieldControl>
                    <Label htmlFor="hoa">Monthly Homeowner's Association costs (HOA)</Label>
                    <BWCurrencyInput
                        fullWidth
                        id="hoa"
                        defaultValue={0}
                        value={hoa}
                        showFractions={false}
                        onChange={(value: number) =>
                            this.props.handleChangeCardAttribute(drawerProps.card, value, 'hoa', 'currency')
                        }
                    />
                </FieldControl>

                <FieldControl>
                    <Label htmlFor="renovation">Total renovation budget</Label>
                    <BWCurrencyInput
                        fullWidth
                        id="renovation"
                        defaultValue={0}
                        value={renovation_budget}
                        showFractions={false}
                        onChange={(value: number) =>
                            this.props.handleChangeCardAttribute(
                                drawerProps.card,
                                value,
                                'renovation_budget',
                                'currency'
                            )
                        }
                    />
                </FieldControl>
            </div>
        );

        const insurance: string = down_payment_percent < 20 ? LiteralsService.getLiteral('BuyHome.insurance') : '';
        const info: JSX.Element = _.isFinite(down_payment_percent) ? (
            <InfoCallout
                noMargin
                text={
                    <span>
                        {LiteralsService.getLiteral('BuyHome.downPayment', [down_payment_percent])} {insurance}
                    </span>
                }
            />
        ) : null;

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.props.handleUpdateCard}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<SoldSignIcon />}
                introContent={LiteralsService.getLiteral('BuyHome.intro')}
                learnMoreContent={LiteralsService.getLiteral('BuyHome.learnMore')}
                learnMoreUrl={helpUrl}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                startDateLabel="When do you want to buy a home?"
            >
                <Output
                    title="Planning for a house cost of:"
                    main={FormatService.format(purchase_price)}
                    subtext={`${FormatService.formatCompactMoney(down_payment)} down and ${FormatService.format(
                        monthly_payment
                    )} per month. ${feesString}`}
                    canAfford={canAfford}
                />

                <BwSlider
                    id="home-price-slider"
                    label="Home price"
                    value={purchase_price}
                    formattedValue={FormatService.formatCompactMoney(purchase_price)}
                    step={10000}
                    min={0}
                    max={3000000}
                    onChange={(value: any) => this.props.handleChangeCardAttribute(card, value, 'purchase_price')}
                    key="home-price-slider"
                    inputToggleText="Add a precise cost"
                />

                <div className={layoutStyles.drawerSection}>
                    <BwSlider
                        id="down-payment-slider"
                        label="Down payment"
                        value={down_payment}
                        formattedValue={FormatService.formatCompactMoney(down_payment)}
                        step={1000}
                        min={0}
                        max={600000}
                        onChange={(value: any) => this.handleSaveCost(value)}
                        onDone={(value: any) => this.handleUpdateSavingsGoal(value)}
                        key="down-payment-slider"
                        inputToggleText="Add a precise cost"
                    />
                    {info}
                </div>

                <BwSlider
                    id="interest-rate-slider"
                    label="Interest rate"
                    value={mortgage_interest}
                    formattedValue={mortgage_interest ? FormatService.formatPercent(Number(mortgage_interest), 2) : 0}
                    step={0.05}
                    min={0}
                    max={10}
                    onChange={async (value: any) => {
                        await this.props.handleChangeCardAttribute(card, Number(value), 'mortgage_interest');
                    }}
                    key="interest-rate-slider"
                    isPercentage
                />

                <BWRadioGroup
                    label="Loan Term"
                    name="mortgage_type"
                    radioOptions={[
                        { id: 'mortgage-15', value: '180', label: '15 year fixed' },
                        { id: 'mortgage-30', value: '360', label: '30 year fixed' }
                    ]}
                    onChange={(e: any) => {
                        this.props.handleChangeCardAttribute(card, e.target.value, 'mortgage_type', 'number');
                    }}
                    defaultChecked={mortgage_type.toString()}
                />

                <Collapsible>
                    <CollapsibleHeading
                        id="sellExistingProperty"
                        labels={{
                            collapsed: LiteralsService.getLiteral('BuyHome.sellExisting'),
                            expanded: LiteralsService.getLiteral('BuyHome.sellExisting')
                        }}
                    >
                        {LiteralsService.getLiteral('BuyHome.sellExisting')}
                    </CollapsibleHeading>
                    <CollapsibleContent
                        labelledBy="sellExistingProperty"
                        theme={{ backgroundColor: 'white !important' }}
                    >
                        {propertyDiv}
                    </CollapsibleContent>
                </Collapsible>

                <Collapsible>
                    <CollapsibleHeading
                        id="additionalOptions"
                        labels={{
                            collapsed: LiteralsService.getLiteral('BuyHome.additionalOptions'),
                            expanded: LiteralsService.getLiteral('BuyHome.additionalOptions')
                        }}
                    >
                        {LiteralsService.getLiteral('BuyHome.additionalOptions')}
                    </CollapsibleHeading>
                    <CollapsibleContent labelledBy="additionalOptions" theme={{ backgroundColor: 'white !important' }}>
                        {advancedDiv}
                    </CollapsibleContent>
                </Collapsible>

                <SavingsGoal
                    drawerProps={drawerProps}
                    user={this.props.user}
                    setDrawer={this.props.setDrawer}
                    handleOpenSignup={() => {
                        console.warn('Deprecated: handleOpenSignup (Buy Home)');
                        this.props.handleOpenSignup();
                    }}
                    handleCreateLinkedSavingsCard={this.props.handleCreateLinkedSavingsCard}
                    handleChangeCardAttribute={this.props.handleChangeCardAttribute}
                    handleUpdateCard={this.props.handleUpdateCard}
                    handleDeleteCard={this.props.handleDeleteCard}
                />
            </GoalDrawer>
        );
    }

    private handleSaveCost(value: number): void {
        this.props.handleChangeCardAttribute(this.props.drawerProps.card, value, 'down_payment');
    }

    private handleUpdateSavingsGoal(value: number): void {
        if (CardService.hasSavingsGoal(this.props)) {
            const updatedCard: CardModel = CardService.getSavingsGoal(this.props);
            _.set(updatedCard, 'attributes.targetBalance', value);
            this.props.handleUpdateCard(updatedCard);
        }
    }
}
